import { client } from '../services/client';
import {
  BattleSession,
  BattleJoinRequest,
  BattleChatMessage,
  BattleRole,
  BattleTokenResponse,
} from '../interfaces/battleInterfaces';

// ── Session CRUD ─────────────────────────────────────────────────────────────

export async function createBattleSession(title: string, hostId: string): Promise<BattleSession> {
  const roomName = `battle-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const { data, error } = await client
    .from('battle_sessions')
    .insert({ title, host_id: hostId, room_name: roomName })
    .select()
    .single();
  if (error) throw new Error('createBattleSession: ' + error.message);
  return data as BattleSession;
}

export async function fetchBattleSession(sessionId: string): Promise<BattleSession> {
  const { data, error } = await client
    .from('battle_sessions')
    .select('*')
    .eq('id', sessionId)
    .single();
  if (error) throw new Error('fetchBattleSession: ' + error.message);
  return data as BattleSession;
}

export async function updateSessionStatus(
  sessionId: string,
  status: BattleSession['status']
): Promise<void> {
  const { error } = await client.from('battle_sessions').update({ status }).eq('id', sessionId);
  if (error) throw new Error('updateSessionStatus: ' + error.message);
}

export async function pinParticipant(sessionId: string, userId: string | null): Promise<void> {
  const { error } = await client
    .from('battle_sessions')
    .update({ pinned_participant: userId })
    .eq('id', sessionId);
  if (error) throw new Error('pinParticipant: ' + error.message);
}

// ── Join requests ─────────────────────────────────────────────────────────────

export async function submitJoinRequest(
  sessionId: string,
  userId: string,
  username: string,
  avatarUrl: string | null
): Promise<void> {
  const { error } = await client
    .from('battle_join_requests')
    .insert({ session_id: sessionId, user_id: userId, username, avatar_url: avatarUrl });
  if (error && !error.message.includes('duplicate')) {
    throw new Error('submitJoinRequest: ' + error.message);
  }
}

export async function respondToJoinRequest(
  requestId: string,
  status: 'approved' | 'denied'
): Promise<void> {
  const { error } = await client
    .from('battle_join_requests')
    .update({ status })
    .eq('id', requestId);
  if (error) throw new Error('respondToJoinRequest: ' + error.message);
}

export async function fetchJoinRequests(sessionId: string): Promise<BattleJoinRequest[]> {
  const { data, error } = await client
    .from('battle_join_requests')
    .select('*')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true });
  if (error) throw new Error('fetchJoinRequests: ' + error.message);
  return (data ?? []) as BattleJoinRequest[];
}

// ── Chat ──────────────────────────────────────────────────────────────────────

export async function fetchBattleChat(sessionId: string): Promise<BattleChatMessage[]> {
  const { data, error } = await client
    .from('battle_chat')
    .select('*')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true })
    .limit(200);
  if (error) throw new Error('fetchBattleChat: ' + error.message);
  return (data ?? []) as BattleChatMessage[];
}

export async function sendBattleChatMessage(
  sessionId: string,
  userId: string,
  username: string,
  avatarUrl: string | null,
  message: string
): Promise<void> {
  const { error } = await client
    .from('battle_chat')
    .insert({ session_id: sessionId, user_id: userId, username, avatar_url: avatarUrl, message });
  if (error) throw new Error('sendBattleChatMessage: ' + error.message);
}

// ── LiveKit token ─────────────────────────────────────────────────────────────

export async function fetchBattleToken(
  sessionId: string,
  role: BattleRole
): Promise<BattleTokenResponse> {
  const {
    data: { session },
  } = await client.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(process.env.REACT_APP_SUPABASE_URL + '/functions/v1/battle-token', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + session.access_token,
    },
    body: JSON.stringify({ session_id: sessionId, role }),
  });

  if (!res.ok) throw new Error('fetchBattleToken (' + res.status + '): ' + (await res.text()));
  return res.json() as Promise<BattleTokenResponse>;
}
